/**
 * OWL2 Axiom Types
 *
 * Defines all axiom types from the OWL2 specification:
 * - Declaration
 * - Class axioms (4): SubClassOf, EquivalentClasses, DisjointClasses, DisjointUnion
 * - Object property axioms (13): SubObjectPropertyOf, EquivalentObjectProperties,
 *   DisjointObjectProperties, InverseObjectProperties, ObjectPropertyDomain, ObjectPropertyRange,
 *   FunctionalObjectProperty, InverseFunctionalObjectProperty, ReflexiveObjectProperty,
 *   IrreflexiveObjectProperty, SymmetricObjectProperty, AsymmetricObjectProperty, TransitiveObjectProperty
 * - Data property axioms (6): SubDataPropertyOf, EquivalentDataProperties, DisjointDataProperties,
 *   DataPropertyDomain, DataPropertyRange, FunctionalDataProperty
 * - Datatype definition
 * - HasKey
 * - Assertions (7): SameIndividual, DifferentIndividuals, ClassAssertion,
 *   ObjectPropertyAssertion, NegativeObjectPropertyAssertion,
 *   DataPropertyAssertion, NegativeDataPropertyAssertion
 * - Annotation axioms (4): AnnotationAssertion, SubAnnotationPropertyOf,
 *   AnnotationPropertyDomain, AnnotationPropertyRange
 */

import type { IRI } from './IRI.ts';
import type { Literal } from './Literal.ts';
import type { Entity, Individual } from './Entity.ts';
import type { ClassExpression, Class } from './ClassExpression.ts';
import type { DataRange, Datatype } from './DataRange.ts';
import type {
    ObjectPropertyExpression,
    DataPropertyExpression,
    SubObjectPropertyExpression
} from './PropertyExpression.ts';
import type { Annotation, AnnotationProperty, AnnotationSubject, AnnotationValue } from './Annotation.ts';

// =============================================================================
// Axiom Type Union
// =============================================================================

/**
 * All possible axiom types.
 */
export type AxiomType =
    // Declaration
    | 'Declaration'
    // Class axioms
    | 'SubClassOf'
    | 'EquivalentClasses'
    | 'DisjointClasses'
    | 'DisjointUnion'
    // Object property axioms
    | 'SubObjectPropertyOf'
    | 'EquivalentObjectProperties'
    | 'DisjointObjectProperties'
    | 'InverseObjectProperties'
    | 'ObjectPropertyDomain'
    | 'ObjectPropertyRange'
    | 'FunctionalObjectProperty'
    | 'InverseFunctionalObjectProperty'
    | 'ReflexiveObjectProperty'
    | 'IrreflexiveObjectProperty'
    | 'SymmetricObjectProperty'
    | 'AsymmetricObjectProperty'
    | 'TransitiveObjectProperty'
    // Data property axioms
    | 'SubDataPropertyOf'
    | 'EquivalentDataProperties'
    | 'DisjointDataProperties'
    | 'DataPropertyDomain'
    | 'DataPropertyRange'
    | 'FunctionalDataProperty'
    // Datatype definition
    | 'DatatypeDefinition'
    // HasKey
    | 'HasKey'
    // Assertions
    | 'SameIndividual'
    | 'DifferentIndividuals'
    | 'ClassAssertion'
    | 'ObjectPropertyAssertion'
    | 'NegativeObjectPropertyAssertion'
    | 'DataPropertyAssertion'
    | 'NegativeDataPropertyAssertion'
    // Annotation axioms
    | 'AnnotationAssertion'
    | 'SubAnnotationPropertyOf'
    | 'AnnotationPropertyDomain'
    | 'AnnotationPropertyRange';

/**
 * Base interface for all axioms.
 * All axioms can have annotations.
 */
interface AxiomBase {
    readonly axiomType: AxiomType;
    readonly annotations?: readonly Annotation[];
}

// =============================================================================
// Declaration
// =============================================================================

/**
 * Entity declaration.
 * Declares the existence of an entity in the ontology.
 * OWL2: Declaration(Entity)
 */
export interface Declaration extends AxiomBase {
    readonly axiomType: 'Declaration';
    readonly entity: Entity;
}

// =============================================================================
// Class Axioms
// =============================================================================

/**
 * Subclass axiom.
 * States that every instance of subClass is also an instance of superClass.
 * OWL2: SubClassOf(CE1 CE2)
 */
export interface SubClassOf extends AxiomBase {
    readonly axiomType: 'SubClassOf';
    readonly subClass: ClassExpression;
    readonly superClass: ClassExpression;
}

/**
 * Equivalent classes axiom.
 * States that the class expressions have exactly the same instances.
 * OWL2: EquivalentClasses(CE1 ... CEn) - requires at least 2 class expressions.
 */
export interface EquivalentClasses extends AxiomBase {
    readonly axiomType: 'EquivalentClasses';
    readonly classExpressions: readonly [ClassExpression, ClassExpression, ...ClassExpression[]];
}

/**
 * Disjoint classes axiom.
 * States that the class expressions have no instances in common.
 * OWL2: DisjointClasses(CE1 ... CEn) - requires at least 2 class expressions.
 */
export interface DisjointClasses extends AxiomBase {
    readonly axiomType: 'DisjointClasses';
    readonly classExpressions: readonly [ClassExpression, ClassExpression, ...ClassExpression[]];
}

/**
 * Disjoint union axiom.
 * States that a class is the disjoint union of the given class expressions.
 * OWL2: DisjointUnion(C CE1 ... CEn) - requires at least 2 class expressions in the union.
 */
export interface DisjointUnion extends AxiomBase {
    readonly axiomType: 'DisjointUnion';
    readonly disjointClass: Class;
    readonly classExpressions: readonly [ClassExpression, ClassExpression, ...ClassExpression[]];
}

/**
 * Union of class axiom types.
 */
export type ClassAxiom = SubClassOf | EquivalentClasses | DisjointClasses | DisjointUnion;

// =============================================================================
// Object Property Axioms
// =============================================================================

/**
 * Sub object property axiom.
 * States that subProperty is a sub-property of superProperty.
 * Can also express property chains.
 * OWL2: SubObjectPropertyOf(OPE1 OPE2) or SubObjectPropertyOf(ObjectPropertyChain(...) OPE)
 */
export interface SubObjectPropertyOf extends AxiomBase {
    readonly axiomType: 'SubObjectPropertyOf';
    readonly subProperty: SubObjectPropertyExpression;
    readonly superProperty: ObjectPropertyExpression;
}

/**
 * Equivalent object properties axiom.
 * OWL2: EquivalentObjectProperties(OPE1 ... OPEn) - requires at least 2 properties.
 */
export interface EquivalentObjectProperties extends AxiomBase {
    readonly axiomType: 'EquivalentObjectProperties';
    readonly properties: readonly [ObjectPropertyExpression, ObjectPropertyExpression, ...ObjectPropertyExpression[]];
}

/**
 * Disjoint object properties axiom.
 * OWL2: DisjointObjectProperties(OPE1 ... OPEn) - requires at least 2 properties.
 */
export interface DisjointObjectProperties extends AxiomBase {
    readonly axiomType: 'DisjointObjectProperties';
    readonly properties: readonly [ObjectPropertyExpression, ObjectPropertyExpression, ...ObjectPropertyExpression[]];
}

/**
 * Inverse object properties axiom.
 * States that two properties are inverses of each other.
 * OWL2: InverseObjectProperties(OPE1 OPE2)
 */
export interface InverseObjectProperties extends AxiomBase {
    readonly axiomType: 'InverseObjectProperties';
    readonly property1: ObjectPropertyExpression;
    readonly property2: ObjectPropertyExpression;
}

/**
 * Object property domain axiom.
 * States that the domain of the property is the given class expression.
 * OWL2: ObjectPropertyDomain(OPE CE)
 */
export interface ObjectPropertyDomain extends AxiomBase {
    readonly axiomType: 'ObjectPropertyDomain';
    readonly property: ObjectPropertyExpression;
    readonly domain: ClassExpression;
}

/**
 * Object property range axiom.
 * States that the range of the property is the given class expression.
 * OWL2: ObjectPropertyRange(OPE CE)
 */
export interface ObjectPropertyRange extends AxiomBase {
    readonly axiomType: 'ObjectPropertyRange';
    readonly property: ObjectPropertyExpression;
    readonly range: ClassExpression;
}

/**
 * Functional object property axiom.
 * States that the property is functional (each individual has at most one value).
 * OWL2: FunctionalObjectProperty(OPE)
 */
export interface FunctionalObjectProperty extends AxiomBase {
    readonly axiomType: 'FunctionalObjectProperty';
    readonly property: ObjectPropertyExpression;
}

/**
 * Inverse functional object property axiom.
 * States that the property is inverse functional.
 * OWL2: InverseFunctionalObjectProperty(OPE)
 */
export interface InverseFunctionalObjectProperty extends AxiomBase {
    readonly axiomType: 'InverseFunctionalObjectProperty';
    readonly property: ObjectPropertyExpression;
}

/**
 * Reflexive object property axiom.
 * States that every individual is related to itself via the property.
 * OWL2: ReflexiveObjectProperty(OPE)
 */
export interface ReflexiveObjectProperty extends AxiomBase {
    readonly axiomType: 'ReflexiveObjectProperty';
    readonly property: ObjectPropertyExpression;
}

/**
 * Irreflexive object property axiom.
 * States that no individual is related to itself via the property.
 * OWL2: IrreflexiveObjectProperty(OPE)
 */
export interface IrreflexiveObjectProperty extends AxiomBase {
    readonly axiomType: 'IrreflexiveObjectProperty';
    readonly property: ObjectPropertyExpression;
}

/**
 * Symmetric object property axiom.
 * States that if a relates to b, then b relates to a.
 * OWL2: SymmetricObjectProperty(OPE)
 */
export interface SymmetricObjectProperty extends AxiomBase {
    readonly axiomType: 'SymmetricObjectProperty';
    readonly property: ObjectPropertyExpression;
}

/**
 * Asymmetric object property axiom.
 * States that if a relates to b, then b does not relate to a.
 * OWL2: AsymmetricObjectProperty(OPE)
 */
export interface AsymmetricObjectProperty extends AxiomBase {
    readonly axiomType: 'AsymmetricObjectProperty';
    readonly property: ObjectPropertyExpression;
}

/**
 * Transitive object property axiom.
 * States that if a relates to b and b relates to c, then a relates to c.
 * OWL2: TransitiveObjectProperty(OPE)
 */
export interface TransitiveObjectProperty extends AxiomBase {
    readonly axiomType: 'TransitiveObjectProperty';
    readonly property: ObjectPropertyExpression;
}

/**
 * Union of object property axiom types.
 */
export type ObjectPropertyAxiom =
    | SubObjectPropertyOf
    | EquivalentObjectProperties
    | DisjointObjectProperties
    | InverseObjectProperties
    | ObjectPropertyDomain
    | ObjectPropertyRange
    | FunctionalObjectProperty
    | InverseFunctionalObjectProperty
    | ReflexiveObjectProperty
    | IrreflexiveObjectProperty
    | SymmetricObjectProperty
    | AsymmetricObjectProperty
    | TransitiveObjectProperty;

// =============================================================================
// Data Property Axioms
// =============================================================================

/**
 * Sub data property axiom.
 * OWL2: SubDataPropertyOf(DPE1 DPE2)
 */
export interface SubDataPropertyOf extends AxiomBase {
    readonly axiomType: 'SubDataPropertyOf';
    readonly subProperty: DataPropertyExpression;
    readonly superProperty: DataPropertyExpression;
}

/**
 * Equivalent data properties axiom.
 * OWL2: EquivalentDataProperties(DPE1 ... DPEn) - requires at least 2 properties.
 */
export interface EquivalentDataProperties extends AxiomBase {
    readonly axiomType: 'EquivalentDataProperties';
    readonly properties: readonly [DataPropertyExpression, DataPropertyExpression, ...DataPropertyExpression[]];
}

/**
 * Disjoint data properties axiom.
 * OWL2: DisjointDataProperties(DPE1 ... DPEn) - requires at least 2 properties.
 */
export interface DisjointDataProperties extends AxiomBase {
    readonly axiomType: 'DisjointDataProperties';
    readonly properties: readonly [DataPropertyExpression, DataPropertyExpression, ...DataPropertyExpression[]];
}

/**
 * Data property domain axiom.
 * OWL2: DataPropertyDomain(DPE CE)
 */
export interface DataPropertyDomain extends AxiomBase {
    readonly axiomType: 'DataPropertyDomain';
    readonly property: DataPropertyExpression;
    readonly domain: ClassExpression;
}

/**
 * Data property range axiom.
 * OWL2: DataPropertyRange(DPE DR)
 */
export interface DataPropertyRange extends AxiomBase {
    readonly axiomType: 'DataPropertyRange';
    readonly property: DataPropertyExpression;
    readonly range: DataRange;
}

/**
 * Functional data property axiom.
 * OWL2: FunctionalDataProperty(DPE)
 */
export interface FunctionalDataProperty extends AxiomBase {
    readonly axiomType: 'FunctionalDataProperty';
    readonly property: DataPropertyExpression;
}

/**
 * Union of data property axiom types.
 */
export type DataPropertyAxiom =
    | SubDataPropertyOf
    | EquivalentDataProperties
    | DisjointDataProperties
    | DataPropertyDomain
    | DataPropertyRange
    | FunctionalDataProperty;

// =============================================================================
// Datatype Definition
// =============================================================================

/**
 * Datatype definition axiom.
 * Defines a datatype as equivalent to a data range.
 * OWL2: DatatypeDefinition(DT DR)
 */
export interface DatatypeDefinition extends AxiomBase {
    readonly axiomType: 'DatatypeDefinition';
    readonly datatype: Datatype;
    readonly dataRange: DataRange;
}

// =============================================================================
// HasKey
// =============================================================================

/**
 * HasKey axiom.
 * Specifies that the given properties are a key for the class expression.
 * OWL2: HasKey(CE (OPE1 ... OPEm) (DPE1 ... DPEn))
 */
export interface HasKey extends AxiomBase {
    readonly axiomType: 'HasKey';
    readonly classExpression: ClassExpression;
    readonly objectPropertyExpressions: readonly ObjectPropertyExpression[];
    readonly dataPropertyExpressions: readonly DataPropertyExpression[];
}

// =============================================================================
// Assertions (ABox Axioms)
// =============================================================================

/**
 * Same individual axiom.
 * States that the given individuals are the same.
 * OWL2: SameIndividual(a1 ... an) - requires at least 2 individuals.
 */
export interface SameIndividual extends AxiomBase {
    readonly axiomType: 'SameIndividual';
    readonly individuals: readonly [Individual, Individual, ...Individual[]];
}

/**
 * Different individuals axiom.
 * States that the given individuals are all different from each other.
 * OWL2: DifferentIndividuals(a1 ... an) - requires at least 2 individuals.
 */
export interface DifferentIndividuals extends AxiomBase {
    readonly axiomType: 'DifferentIndividuals';
    readonly individuals: readonly [Individual, Individual, ...Individual[]];
}

/**
 * Class assertion axiom.
 * States that an individual is an instance of a class expression.
 * OWL2: ClassAssertion(CE a)
 */
export interface ClassAssertion extends AxiomBase {
    readonly axiomType: 'ClassAssertion';
    readonly classExpression: ClassExpression;
    readonly individual: Individual;
}

/**
 * Object property assertion axiom.
 * States that a source individual is related to a target individual via a property.
 * OWL2: ObjectPropertyAssertion(OPE a1 a2)
 */
export interface ObjectPropertyAssertion extends AxiomBase {
    readonly axiomType: 'ObjectPropertyAssertion';
    readonly property: ObjectPropertyExpression;
    readonly sourceIndividual: Individual;
    readonly targetIndividual: Individual;
}

/**
 * Negative object property assertion axiom.
 * States that a source individual is NOT related to a target individual via a property.
 * OWL2: NegativeObjectPropertyAssertion(OPE a1 a2)
 */
export interface NegativeObjectPropertyAssertion extends AxiomBase {
    readonly axiomType: 'NegativeObjectPropertyAssertion';
    readonly property: ObjectPropertyExpression;
    readonly sourceIndividual: Individual;
    readonly targetIndividual: Individual;
}

/**
 * Data property assertion axiom.
 * States that an individual has a literal value for a property.
 * OWL2: DataPropertyAssertion(DPE a lt)
 */
export interface DataPropertyAssertion extends AxiomBase {
    readonly axiomType: 'DataPropertyAssertion';
    readonly property: DataPropertyExpression;
    readonly sourceIndividual: Individual;
    readonly targetValue: Literal;
}

/**
 * Negative data property assertion axiom.
 * States that an individual does NOT have a literal value for a property.
 * OWL2: NegativeDataPropertyAssertion(DPE a lt)
 */
export interface NegativeDataPropertyAssertion extends AxiomBase {
    readonly axiomType: 'NegativeDataPropertyAssertion';
    readonly property: DataPropertyExpression;
    readonly sourceIndividual: Individual;
    readonly targetValue: Literal;
}

/**
 * Union of assertion axiom types.
 */
export type Assertion =
    | SameIndividual
    | DifferentIndividuals
    | ClassAssertion
    | ObjectPropertyAssertion
    | NegativeObjectPropertyAssertion
    | DataPropertyAssertion
    | NegativeDataPropertyAssertion;

// =============================================================================
// Annotation Axioms
// =============================================================================

/**
 * Annotation assertion axiom.
 * Asserts an annotation on a subject.
 * OWL2: AnnotationAssertion(AP s av)
 */
export interface AnnotationAssertion extends AxiomBase {
    readonly axiomType: 'AnnotationAssertion';
    readonly property: AnnotationProperty;
    readonly subject: AnnotationSubject;
    readonly value: AnnotationValue;
}

/**
 * Sub annotation property axiom.
 * OWL2: SubAnnotationPropertyOf(AP1 AP2)
 */
export interface SubAnnotationPropertyOf extends AxiomBase {
    readonly axiomType: 'SubAnnotationPropertyOf';
    readonly subProperty: AnnotationProperty;
    readonly superProperty: AnnotationProperty;
}

/**
 * Annotation property domain axiom.
 * OWL2: AnnotationPropertyDomain(AP IRI)
 */
export interface AnnotationPropertyDomain extends AxiomBase {
    readonly axiomType: 'AnnotationPropertyDomain';
    readonly property: AnnotationProperty;
    readonly domain: IRI;
}

/**
 * Annotation property range axiom.
 * OWL2: AnnotationPropertyRange(AP IRI)
 */
export interface AnnotationPropertyRange extends AxiomBase {
    readonly axiomType: 'AnnotationPropertyRange';
    readonly property: AnnotationProperty;
    readonly range: IRI;
}

/**
 * Union of annotation axiom types.
 */
export type AnnotationAxiom =
    | AnnotationAssertion
    | SubAnnotationPropertyOf
    | AnnotationPropertyDomain
    | AnnotationPropertyRange;

// =============================================================================
// Union of All Axiom Types
// =============================================================================

/**
 * Discriminated union of all OWL2 axiom types.
 * The 'axiomType' field distinguishes between axiom kinds.
 */
export type Axiom =
    | Declaration
    | ClassAxiom
    | ObjectPropertyAxiom
    | DataPropertyAxiom
    | DatatypeDefinition
    | HasKey
    | Assertion
    | AnnotationAxiom;

// =============================================================================
// Type Guards
// =============================================================================

// Declaration
export function isDeclaration(axiom: Axiom): axiom is Declaration {
    return axiom.axiomType === 'Declaration';
}

// Class axioms
export function isSubClassOf(axiom: Axiom): axiom is SubClassOf {
    return axiom.axiomType === 'SubClassOf';
}

export function isEquivalentClasses(axiom: Axiom): axiom is EquivalentClasses {
    return axiom.axiomType === 'EquivalentClasses';
}

export function isDisjointClasses(axiom: Axiom): axiom is DisjointClasses {
    return axiom.axiomType === 'DisjointClasses';
}

export function isDisjointUnion(axiom: Axiom): axiom is DisjointUnion {
    return axiom.axiomType === 'DisjointUnion';
}

export function isClassAxiom(axiom: Axiom): axiom is ClassAxiom {
    return (
        axiom.axiomType === 'SubClassOf' ||
        axiom.axiomType === 'EquivalentClasses' ||
        axiom.axiomType === 'DisjointClasses' ||
        axiom.axiomType === 'DisjointUnion'
    );
}

// Object property axioms
export function isSubObjectPropertyOf(axiom: Axiom): axiom is SubObjectPropertyOf {
    return axiom.axiomType === 'SubObjectPropertyOf';
}

export function isEquivalentObjectProperties(axiom: Axiom): axiom is EquivalentObjectProperties {
    return axiom.axiomType === 'EquivalentObjectProperties';
}

export function isDisjointObjectProperties(axiom: Axiom): axiom is DisjointObjectProperties {
    return axiom.axiomType === 'DisjointObjectProperties';
}

export function isInverseObjectProperties(axiom: Axiom): axiom is InverseObjectProperties {
    return axiom.axiomType === 'InverseObjectProperties';
}

export function isObjectPropertyDomain(axiom: Axiom): axiom is ObjectPropertyDomain {
    return axiom.axiomType === 'ObjectPropertyDomain';
}

export function isObjectPropertyRange(axiom: Axiom): axiom is ObjectPropertyRange {
    return axiom.axiomType === 'ObjectPropertyRange';
}

export function isFunctionalObjectProperty(axiom: Axiom): axiom is FunctionalObjectProperty {
    return axiom.axiomType === 'FunctionalObjectProperty';
}

export function isInverseFunctionalObjectProperty(axiom: Axiom): axiom is InverseFunctionalObjectProperty {
    return axiom.axiomType === 'InverseFunctionalObjectProperty';
}

export function isReflexiveObjectProperty(axiom: Axiom): axiom is ReflexiveObjectProperty {
    return axiom.axiomType === 'ReflexiveObjectProperty';
}

export function isIrreflexiveObjectProperty(axiom: Axiom): axiom is IrreflexiveObjectProperty {
    return axiom.axiomType === 'IrreflexiveObjectProperty';
}

export function isSymmetricObjectProperty(axiom: Axiom): axiom is SymmetricObjectProperty {
    return axiom.axiomType === 'SymmetricObjectProperty';
}

export function isAsymmetricObjectProperty(axiom: Axiom): axiom is AsymmetricObjectProperty {
    return axiom.axiomType === 'AsymmetricObjectProperty';
}

export function isTransitiveObjectProperty(axiom: Axiom): axiom is TransitiveObjectProperty {
    return axiom.axiomType === 'TransitiveObjectProperty';
}

export function isObjectPropertyAxiom(axiom: Axiom): axiom is ObjectPropertyAxiom {
    return (
        axiom.axiomType === 'SubObjectPropertyOf' ||
        axiom.axiomType === 'EquivalentObjectProperties' ||
        axiom.axiomType === 'DisjointObjectProperties' ||
        axiom.axiomType === 'InverseObjectProperties' ||
        axiom.axiomType === 'ObjectPropertyDomain' ||
        axiom.axiomType === 'ObjectPropertyRange' ||
        axiom.axiomType === 'FunctionalObjectProperty' ||
        axiom.axiomType === 'InverseFunctionalObjectProperty' ||
        axiom.axiomType === 'ReflexiveObjectProperty' ||
        axiom.axiomType === 'IrreflexiveObjectProperty' ||
        axiom.axiomType === 'SymmetricObjectProperty' ||
        axiom.axiomType === 'AsymmetricObjectProperty' ||
        axiom.axiomType === 'TransitiveObjectProperty'
    );
}

// Data property axioms
export function isSubDataPropertyOf(axiom: Axiom): axiom is SubDataPropertyOf {
    return axiom.axiomType === 'SubDataPropertyOf';
}

export function isEquivalentDataProperties(axiom: Axiom): axiom is EquivalentDataProperties {
    return axiom.axiomType === 'EquivalentDataProperties';
}

export function isDisjointDataProperties(axiom: Axiom): axiom is DisjointDataProperties {
    return axiom.axiomType === 'DisjointDataProperties';
}

export function isDataPropertyDomain(axiom: Axiom): axiom is DataPropertyDomain {
    return axiom.axiomType === 'DataPropertyDomain';
}

export function isDataPropertyRange(axiom: Axiom): axiom is DataPropertyRange {
    return axiom.axiomType === 'DataPropertyRange';
}

export function isFunctionalDataProperty(axiom: Axiom): axiom is FunctionalDataProperty {
    return axiom.axiomType === 'FunctionalDataProperty';
}

export function isDataPropertyAxiom(axiom: Axiom): axiom is DataPropertyAxiom {
    return (
        axiom.axiomType === 'SubDataPropertyOf' ||
        axiom.axiomType === 'EquivalentDataProperties' ||
        axiom.axiomType === 'DisjointDataProperties' ||
        axiom.axiomType === 'DataPropertyDomain' ||
        axiom.axiomType === 'DataPropertyRange' ||
        axiom.axiomType === 'FunctionalDataProperty'
    );
}

// Datatype definition
export function isDatatypeDefinition(axiom: Axiom): axiom is DatatypeDefinition {
    return axiom.axiomType === 'DatatypeDefinition';
}

// HasKey
export function isHasKey(axiom: Axiom): axiom is HasKey {
    return axiom.axiomType === 'HasKey';
}

// Assertions
export function isSameIndividual(axiom: Axiom): axiom is SameIndividual {
    return axiom.axiomType === 'SameIndividual';
}

export function isDifferentIndividuals(axiom: Axiom): axiom is DifferentIndividuals {
    return axiom.axiomType === 'DifferentIndividuals';
}

export function isClassAssertion(axiom: Axiom): axiom is ClassAssertion {
    return axiom.axiomType === 'ClassAssertion';
}

export function isObjectPropertyAssertion(axiom: Axiom): axiom is ObjectPropertyAssertion {
    return axiom.axiomType === 'ObjectPropertyAssertion';
}

export function isNegativeObjectPropertyAssertion(axiom: Axiom): axiom is NegativeObjectPropertyAssertion {
    return axiom.axiomType === 'NegativeObjectPropertyAssertion';
}

export function isDataPropertyAssertion(axiom: Axiom): axiom is DataPropertyAssertion {
    return axiom.axiomType === 'DataPropertyAssertion';
}

export function isNegativeDataPropertyAssertion(axiom: Axiom): axiom is NegativeDataPropertyAssertion {
    return axiom.axiomType === 'NegativeDataPropertyAssertion';
}

export function isAssertion(axiom: Axiom): axiom is Assertion {
    return (
        axiom.axiomType === 'SameIndividual' ||
        axiom.axiomType === 'DifferentIndividuals' ||
        axiom.axiomType === 'ClassAssertion' ||
        axiom.axiomType === 'ObjectPropertyAssertion' ||
        axiom.axiomType === 'NegativeObjectPropertyAssertion' ||
        axiom.axiomType === 'DataPropertyAssertion' ||
        axiom.axiomType === 'NegativeDataPropertyAssertion'
    );
}

// Annotation axioms
export function isAnnotationAssertion(axiom: Axiom): axiom is AnnotationAssertion {
    return axiom.axiomType === 'AnnotationAssertion';
}

export function isSubAnnotationPropertyOf(axiom: Axiom): axiom is SubAnnotationPropertyOf {
    return axiom.axiomType === 'SubAnnotationPropertyOf';
}

export function isAnnotationPropertyDomain(axiom: Axiom): axiom is AnnotationPropertyDomain {
    return axiom.axiomType === 'AnnotationPropertyDomain';
}

export function isAnnotationPropertyRange(axiom: Axiom): axiom is AnnotationPropertyRange {
    return axiom.axiomType === 'AnnotationPropertyRange';
}

export function isAnnotationAxiom(axiom: Axiom): axiom is AnnotationAxiom {
    return (
        axiom.axiomType === 'AnnotationAssertion' ||
        axiom.axiomType === 'SubAnnotationPropertyOf' ||
        axiom.axiomType === 'AnnotationPropertyDomain' ||
        axiom.axiomType === 'AnnotationPropertyRange'
    );
}
